/**
 * Organization-level audience settings (docs/Decisions.md D63 item 2).
 *
 * What an org admin sets once for the whole organization, as opposed to what
 * is set on each event, video or forum category:
 *
 * - events.publicVisibility: events marked Public are listed on the
 *   organization's public page, where visitors who are not signed in see
 *   their name, date, format and place.
 * - events.showDetailsToVisitors: those visitors can also open the full
 *   description; when off they are asked to sign in to read it. Registering
 *   always needs an account.
 * - videos.memberViewsPerMonth: how many videos each member may play in a
 *   calendar month, 0 for unlimited.
 * - videos.previewVideos: how many of the videos marked Public a visitor or a
 *   signed-in person who is not a member may watch before joining, 0 to 2
 *   (D65 item 1, the owner's answer to OQ-28). Joining gives them the member
 *   allowance above.
 *
 * There is no user limit on any feature: events, the forum and videos are
 * unlimited at the organization level. Event capacity is set on each event,
 * and forum access on each category (members, accredited members or groups).
 * The keys the first version stored (`events.publicAccess`,
 * `events.registeredLimit`, `forum.*`, `videos.publicLimit`,
 * `videos.registeredLimit`, `videos.memberLimit`) never did what their labels
 * said and are ignored on read.
 *
 * Mirror of src/shared/utils/audienceRules.js, which the API enforces;
 * frontend/src/lib/audience.test.js imports both to prove they agree. The
 * form copy (AUDIENCE_COPY) lives only here.
 */

export const MAX_VIEWS_PER_MONTH = 10000;
export const MAX_PREVIEW_VIDEOS = 2;

/** Bounds of each count, and what 0 means for it. */
export const COUNT_LIMITS = Object.freeze({
  memberViewsPerMonth: Object.freeze({ max: MAX_VIEWS_PER_MONTH, zero: 'unlimited' }),
  previewVideos: Object.freeze({ max: MAX_PREVIEW_VIDEOS, zero: 'none' }),
});

export const DEFAULT_AUDIENCE_CONFIG = Object.freeze({
  events: Object.freeze({
    publicVisibility: true,
    showDetailsToVisitors: true,
  }),
  videos: Object.freeze({
    memberViewsPerMonth: 0,
    previewVideos: 2,
  }),
});

/** Field kinds; the order of features and fields is the order the forms show. */
export const AUDIENCE_FIELDS = Object.freeze({
  events: Object.freeze({
    publicVisibility: 'boolean',
    showDetailsToVisitors: 'boolean',
  }),
  videos: Object.freeze({
    memberViewsPerMonth: 'count',
    previewVideos: 'count',
  }),
});

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * The stored row's config with today's defaults under it. Unknown or legacy
 * keys and values of the wrong kind are dropped, so a read never returns
 * something the forms cannot show.
 */
export function normalizeAudienceConfig(stored) {
  const result = {};
  for (const [feature, fields] of Object.entries(AUDIENCE_FIELDS)) {
    const saved = isPlainObject(stored) && isPlainObject(stored[feature]) ? stored[feature] : {};
    result[feature] = {};
    for (const [field, kind] of Object.entries(fields)) {
      const value = saved[field];
      const ok =
        kind === 'boolean'
          ? typeof value === 'boolean'
          : Number.isInteger(value) && value >= 0 && value <= COUNT_LIMITS[field].max;
      result[feature][field] = ok ? value : DEFAULT_AUDIENCE_CONFIG[feature][field];
    }
  }
  return result;
}

/**
 * Validate an update. A feature or field left out keeps its current value
 * (pass the current config). Returns `{ value, fieldErrors }`, where
 * fieldErrors maps `feature.field` (or `feature`) to a message the form shows
 * beside that field.
 */
export function validateAudienceConfig(body, current = DEFAULT_AUDIENCE_CONFIG) {
  const fieldErrors = {};
  const value = normalizeAudienceConfig(current);
  if (!isPlainObject(body)) {
    return { value, fieldErrors: { _: 'Send the settings as an object of features' } };
  }
  for (const [feature, fields] of Object.entries(body)) {
    const allowed = AUDIENCE_FIELDS[feature];
    if (!allowed) {
      fieldErrors[feature] = `${feature} has no organization-level settings`;
      continue;
    }
    if (!isPlainObject(fields)) {
      fieldErrors[feature] = `${feature} must be an object of settings`;
      continue;
    }
    for (const [field, raw] of Object.entries(fields)) {
      const key = `${feature}.${field}`;
      const kind = allowed[field];
      if (!kind) {
        fieldErrors[key] = 'This setting no longer exists';
      } else if (kind === 'boolean') {
        if (typeof raw !== 'boolean') fieldErrors[key] = 'Choose on or off';
        else value[feature][field] = raw;
      } else if (!Number.isInteger(raw)) {
        fieldErrors[key] = `Enter a whole number (0 for ${COUNT_LIMITS[field].zero})`;
      } else if (raw < 0 || raw > COUNT_LIMITS[field].max) {
        fieldErrors[key] = `Enter a number from 0 (${COUNT_LIMITS[field].zero}) to ${COUNT_LIMITS[field].max}`;
      } else {
        value[feature][field] = raw;
      }
    }
  }
  return { value, fieldErrors };
}

/**
 * What each setting means, in the words the forms use. Each label says what
 * the switch does and each description who it affects, so no two read alike.
 */
export const AUDIENCE_COPY = Object.freeze({
  events: {
    title: 'Events',
    summary:
      'Who outside your organization can see your events. Event capacity is set on each event.',
    fields: {
      publicVisibility: {
        label: 'List public events on your public page',
        description:
          'Events you mark Public appear on your organization page, where anyone can see their name, date, format and place without signing in. Off: only signed-in people see them.',
      },
      showDetailsToVisitors: {
        label: 'Show full event descriptions to visitors',
        description:
          'On: visitors who are not signed in can read the whole description of a listed event. Off: they see the name, date and place and are asked to sign in for the rest. Registering always needs an account.',
      },
    },
  },
  videos: {
    title: 'Videos',
    summary:
      'Video views per month for members, and a short preview for people who have not joined yet.',
    fields: {
      memberViewsPerMonth: {
        label: 'Videos each member can play per month',
        description:
          'Counted per member from the 1st of each month. 0 means unlimited.',
      },
      previewVideos: {
        label: 'Public videos a visitor can watch before joining',
        description:
          'Visitors and signed-in people who are not members can watch this many of your videos marked Public, then are asked to join to keep watching. 0 hides your videos from visitors.',
      },
    },
  },
});
